import {
  Bug,
  CompanyMember,
  TaskStatus,
  Sprint,
  User,
  UnassignedUnscheduledTask,
  ViewMode,
  DayTask,
} from "./types";
import {
  checkDateStatus,
  ymd,
  addDays,
  getDaysInMonth,
  isDateInRange,
} from "./dateUtils";

export const getAssigneeInfo = (
  assignToObjects: Array<{ _id: string; fullName: string }>,
  companyMembers: CompanyMember[]
): CompanyMember[] => {
  const assignToIds = assignToObjects.map((a) => a._id);
  return companyMembers.filter((member) => assignToIds.includes(member._id));
};

export const mapBugStatusToTaskStatus = (
  bugStatusObj: { status: string; statusCode: string },
  type: string
): TaskStatus => {
  const status = bugStatusObj.status || bugStatusObj.statusCode;

  if (status === "DONE" || status === "COMPLETED") return "Completed";
  if (status === "IN_PROGRESS" || status === "DOING") return "In Progress";
  if (status === "TODO" || status === "TO_DO") return "Not Started";

  if (type === "BUG") return "In Progress";
  if (type === "STORY") return "Not Started";
  if (type === "DEFECT") return "In Progress";
  return "Not Started";
};

export const mapSeverityToHours = (
  severity: string,
  estimation: string | null
): number => {
  if (estimation && !isNaN(Number(estimation))) {
    return Number(estimation);
  }

  switch (severity?.toLowerCase()) {
    case "high":
      return 8;
    case "medium":
      return 4;
    case "low":
      return 2;
    default:
      return 4;
  }
};

export const buildUsersFromData = (
  companyMembers: CompanyMember[],
  bugs: Bug[],
  sprint: Sprint | undefined,
  viewStart: Date,
  viewEnd: Date,
  viewMode: ViewMode
): {
  users: User[];
  unassignedUnscheduledTasks: UnassignedUnscheduledTask[];
} => {
  if (!bugs || !companyMembers) {
    return { users: [], unassignedUnscheduledTasks: [] };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tasksByUser: Record<string, any[]> = {};
  const unassignedUnscheduledTasks: UnassignedUnscheduledTask[] = [];

  bugs.forEach((bug) => {
    const isUnassigned = !bug.assignTo || bug.assignTo.length === 0;
    const isUnscheduled = !bug.dueDate;
    const dateStatus = checkDateStatus(bug.dueDate, sprint, today);

    const assigneeInfo = isUnassigned
      ? []
      : getAssigneeInfo(bug.assignTo, companyMembers);

    if (isUnassigned || isUnscheduled) {
      unassignedUnscheduledTasks.push({
        id: bug._id,
        taskKey: bug.bugKey,
        title: bug.summary,
        type: bug.type,
        severity: bug.severity,
        status: mapBugStatusToTaskStatus(bug.bugStatus, bug.type),
        assignTo: bug.assignTo || [],
        assigneeInfo,
        dueDate: bug.dueDate,
        hours: mapSeverityToHours(bug.severity, bug.estimation),
        isUnassigned,
        isUnscheduled,
        bugStatus: bug.bugStatus,
        commentCount: bug.commentCount || 0,
        ...dateStatus,
      });
      return;
    }

    // Get all assignees from the bug
    const assignToIds = bug.assignTo.map((a) => a._id);

    // Add task to each assignee's list (even if not in project users)
    assignToIds.forEach((userId) => {
      if (!tasksByUser[userId]) {
        tasksByUser[userId] = [];
      }
      tasksByUser[userId].push({ ...bug, assigneeInfo, ...dateStatus });
    });
  });

  // Get unique user IDs that have tasks
  const userIdsWithTasks = Object.keys(tasksByUser);

  // Filter company members to only those who have tasks
  const usersWithTasks = companyMembers.filter((member) =>
    userIdsWithTasks.includes(member._id)
  );

  const users = usersWithTasks.map((member) => {
    const userTasks = tasksByUser[member._id] || [];
    const tasksByDate: Record<string, DayTask[]> = {};

    if (viewMode === "sprint") {
      // For sprint view, initialize dates from viewStart to viewEnd
      const daysDiff =
        Math.floor(
          (viewEnd.getTime() - viewStart.getTime()) / (1000 * 60 * 60 * 24)
        ) + 1;
      for (let i = 0; i < daysDiff; i++) {
        const dayKey = ymd(addDays(viewStart, i));
        tasksByDate[dayKey] = [];
      }
    } else {
      // For month view
      const daysInMonth = getDaysInMonth(viewStart);
      for (let i = 1; i <= daysInMonth; i++) {
        const dayKey = ymd(
          new Date(viewStart.getFullYear(), viewStart.getMonth(), i)
        );
        tasksByDate[dayKey] = [];
      }
    }

    userTasks.forEach((bug) => {
      if (bug.dueDate) {
        const dueDate = new Date(bug.dueDate);
        const dayKey = ymd(dueDate);

        const shouldShowTask =
          isDateInRange(bug.dueDate, viewStart, viewEnd) ||
          bug.isOverdue ||
          bug.isOutOfSprint;

        if (shouldShowTask && tasksByDate[dayKey] !== undefined) {
          const task: DayTask = {
            id: bug._id,
            taskKey: bug.bugKey,
            title: bug.summary,
            hours: mapSeverityToHours(bug.severity, bug.estimation),
            status: mapBugStatusToTaskStatus(bug.bugStatus, bug.type),
            tag: bug.type === "BUG" ? "bug" : undefined,
            severity: bug.severity,
            type: bug.type,
            dueDate: bug.dueDate,
            assignTo: bug.assignTo,
            assigneeInfo: bug.assigneeInfo,
            isOverdue: bug.isOverdue,
            isOutOfSprint: bug.isOutOfSprint,
          };
          tasksByDate[dayKey].push(task);
        }
      }
    });

    const weeklyCapacity = viewMode === "sprint" ? 40 : 160;

    return {
      id: member._id,
      name:
        member.fullName ||
        `${member.firstName} ${member.lastName}`.trim() ||
        "Unknown User",
      email: member.email,
      avatarUrl: undefined,
      weeklyCapacityHours: weeklyCapacity,
      enabled: member.userStatus === "ACTIVE",
      tasksByDate,
    };
  });

  return {
    users,
    unassignedUnscheduledTasks,
  };
};

export const statusColor = (status: TaskStatus) => {
  switch (status) {
    case "In Progress":
      return { bg: "#FFF0E0", text: "#A15C00", chip: "#EAA85E" };
    case "Not Started":
      return { bg: "#F2F3F5", text: "#4D5562", chip: "#AEB4BE" };
    case "Completed":
      return { bg: "#E7F6EC", text: "#256C3A", chip: "#7AC89B" };
    case "DONE":
      return { bg: "#E7F6EC", text: "#256C3A", chip: "#7AC89B" };
    case "TODO":
      return { bg: "#F2F3F5", text: "#4D5562", chip: "#AEB4BE" };
    case "IN_PROGRESS":
      return { bg: "#FFF0E0", text: "#A15C00", chip: "#EAA85E" };
    case "COMPLETED":
      return { bg: "#E7F6EC", text: "#256C3A", chip: "#7AC89B" };
    default:
      return { bg: "#F2F3F5", text: "#4D5562", chip: "#AEB4BE" };
  }
};

export const getTypeColor = (type?: string) => {
  switch (type) {
    case "BUG":
      return "#DC2626";
    case "STORY":
      return "#059669";
    case "DEFECT":
      return "#D97706";
    case "SUGGESTIONS":
      return "#7C3AED";
    default:
      return "#6B7280";
  }
};

export const getSeverityColor = (severity?: string) => {
  switch (severity) {
    case "Highest":
      return "#D91656";
    case "High":
      return "#EB5B00";
    case "Medium":
      return "#FFB200";
    case "Low":
      return "#003092";
    default:
      return "#6B7280";
  }
};
